import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../contexts/user_context';
import spin_game_service, { SpinGameConfig, SpinGameSession } from '../../services/spin_game_service';
import { WheelComponent } from './wheel_component';
import { ScreenType, useAppContext } from '../../contexts/app_context';
import { useNavigate } from 'react-router-dom';

import light from '../../assets/light.png';
import pin from '../../assets/wheel_pin.png';

import './spin_game.css';

const TIER_COLOR = [
    '#ba4d4e',
    '#1592e8',
    '#14c187',
    '#fc7800',
    '#14c187',
    '#1592e8',
    '#ba4d4e',
    '#1592e8',
    '#14c187',
    '#fc7800',
    '#14c187',
    '#1592e8',
];

const S = 3;
const M = 5;

export const SpinGame: FC = () => {
    const { spins, access_token, update_balance, update_spins } = useUserContext();
    const [config, set_config] = useState<SpinGameConfig | null>(null);
    const [session, set_session] = useState<SpinGameSession | null>(null);
    const [rotation, set_rotation] = useState<number>(0);

    const { cancel_action, screen_type, open_prize_dialog } = useAppContext();

    const spinner_size = useMemo(() => {
        switch (screen_type) {
            case ScreenType.mobile_portrait:
            case ScreenType.mobile_landscape:
                return 280;

            default:
                return 420;
        }
    }, [screen_type]);

    const navigate = useNavigate();

    const fetch_config = useCallback(async () => {
        if (!access_token) return;
        const config = await spin_game_service.get_config(access_token);
        if (config) {
            set_config(config);
            update_spins(config.spins);
        }
    }, [access_token, update_spins]);

    useEffect(() => {
        fetch_config();
    }, [fetch_config]);

    useEffect(() => {
        return () => {
            if (session) {
                update_balance(session.balance);
            }
        };
    }, [session, update_balance]);

    const on_spin = useCallback(async () => {
        update_spins(spins - 1);
        const session = await spin_game_service.get_session(access_token);
        if (session) {
            set_session(session);
            const total_sections = config!.sections.length;
            const slice_angle = 360 / total_sections;
            const chosen_slice_center = session.index * slice_angle + slice_angle / 2;
            set_rotation(Math.floor(rotation / 360) * 360 + (M * 360 - chosen_slice_center));
            setTimeout(() => {
                set_session(null);
                if (!session.section.points) {
                    update_balance(session.balance);
                } else {
                    open_prize_dialog(`${session.section.points}$`);
                }
            }, S * 1000);
        }
    }, [update_spins, spins, access_token, config, rotation, update_balance, open_prize_dialog]);

    const on_get_spins = () => {
        cancel_action();
        navigate('/quests');
    };

    return (
        <div className='vertical-center spin-game'>
            <div className='text-container'>
                <h2>🎡 Spin the Wheel – Win Amazing Prizes! 🎁</h2>
                <p>Try your luck and claim a valuable prize! Click, spin, and win – it’s that easy! 🚀</p>
                <div className='game-info-container '>
                    <div className='counter'>
                        <span>Spins:</span> {spins}
                    </div>
                    <button className='primary-button' onClick={on_get_spins}>Get more!</button>
                </div>
            </div>

            <div className='game-container fog yellow fog-bottom'>
                <div>
                    {config && (
                        <div className='wheel-container '>
                            {spins > 0 && <img className='pin' src={pin} alt='' />}
                            <div
                                style={{
                                    position: 'relative',
                                    width: spinner_size,
                                    height: spinner_size,
                                    opacity: spins > 0 ? 1 : 0.2,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        transition: `transform ${S}s cubic-bezier(0.32, 0.64, 0.45, 1)`,
                                        transform: `rotate(${rotation}deg)`,
                                    }}
                                >
                                    <WheelComponent
                                        sections={config.sections.map((section, index) => ({
                                            text: ` $${section.points}`,
                                            color: TIER_COLOR[(index + 1) % 2 > 0 ? 0 : 3],
                                            stroke: 2,
                                            strokeColor: '#F5BA33',
                                            fontSize: 24,
                                        }))}
                                        fillCenter={'transparent'}
                                        width={spinner_size}
                                        height={spinner_size}
                                        radius={spinner_size / 2}
                                        rotation={0}
                                        borderWidth={10}
                                        rotateText
                                        borderColor={'#F5BA33'}
                                    />
                                </div>
                                {spins > 0 && <img src={light} alt='' className='light' />}
                            </div>
                            {spins <= 0 && (
                                <div className='wheel-earn-spins-text'>
                                    <div>Get free spins on a new quest!</div>
                                    <button onClick={on_get_spins} className='button primary-button'>
                                        Earn spins! <div className='glow' />
                                    </button>
                                </div>
                            )}
                            {!session && spins > 0 && (
                                <button className='shake' onClick={on_spin}>
                                    Spin
                                </button>
                            )}
                            {/* {session && ready && spins > 0 && <button onClick={on_continue}>Continue</button>} */}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
